import Vue from 'vue'
import router from '../../router'

const state = {
	token: localStorage.getItem('portal-token') || '',
	user: JSON.parse(localStorage.getItem('portal-user') || 'null'),
	companies: JSON.parse(localStorage.getItem('portal-companies') || '[]'),
	currentCompanyId: Number(localStorage.getItem('portal-company-id')) || null,
	status: '',
	forcePasswordChange: localStorage.getItem('portal-force-pw') === 'true',
	// Sessione chiusa dal server: il login mostra un avviso invece di comparire senza spiegazioni
	sessionExpired: false,
	unreadNotifications: 0
}

const getters = {
	isPortalAuthenticated: state => !!state.token,
	portalUser: state => state.user,
	portalStatus: state => state.status,
	portalForcePasswordChange: state => state.forcePasswordChange,
	portalUnreadNotifications: state => state.unreadNotifications,
	portalCompanies: state => state.companies,
	portalCurrentCompanyId: state => state.currentCompanyId,
	portalIsMultiCompany: state => state.companies.length > 1,
	portalCurrentCompany: state => state.companies.find(c => c.id == state.currentCompanyId) || null,
	portalNeedsCompanySelection: state => state.companies.length > 1 && !state.currentCompanyId,
	portalRole: state => {
		const company = state.companies.find(c => c.id == state.currentCompanyId)
		return company ? company.role : null
	}
}

const actions = {
	// Con la verifica in due passaggi il login risponde success=false anche
	// quando la password e' giusta: data e' 'insert_otp' oppure il QR da
	// scansionare. Il componente decide il passo successivo.
	// silentClientErrors: credenziali od OTP errati (401), blocco (429) e
	// account disabilitato (403) sono mostrati nel form, non come avviso
	// globale; gli errori del server (5xx, manutenzione) seguono il percorso
	// normale, Sentry compreso.
	PORTAL_LOGIN: ({ commit }, credentials) => {
		return new Promise((resolve, reject) => {
			commit('PORTAL_REQUEST')
			Vue.axios({ url: 'portal/login', data: credentials, method: 'POST', silentClientErrors: true })
				.then(resp => {
					if (resp.data.success === false) {
						commit('PORTAL_ERROR')
						resolve(resp.data)
						return
					}
					commit('PORTAL_SUCCESS', resp.data.data)
					resolve(resp.data)
				})
				.catch(err => {
					commit('PORTAL_ERROR')
					localStorage.removeItem('portal-token')
					reject(err)
				})
		})
	},

	PORTAL_SELECT_COMPANY: ({ commit, state }, companyId) => {
		commit('PORTAL_SET_COMPANY', companyId)
		return Vue.axios({ url: 'portal/select-company', data: { company_id: companyId }, method: 'POST' })
			.then(resp => {
				if (resp.data.success && resp.data.data) {
					commit('PORTAL_UPDATE_COMPANY_DATA', { companyId, data: resp.data.data })
				}
				return resp.data
			})
			.catch(() => {
				// Rollback: rimuovi selezione company se il server ha rifiutato
				state.currentCompanyId = null
				localStorage.removeItem('portal-company-id')
				delete Vue.axios.defaults.headers['X-Portal-Company']
			})
	},

	PORTAL_LOGOUT: ({ commit }) => {
		return new Promise((resolve) => {
			Vue.axios({ url: 'portal/logout', method: 'POST' })
				.then(() => {
					commit('PORTAL_LOGOUT')
					resolve()
				})
				.catch(() => {
					commit('PORTAL_LOGOUT')
					resolve()
				})
		})
	},

	PORTAL_FETCH_PROFILE: ({ commit }) => {
		return new Promise((resolve, reject) => {
			Vue.axios({ url: 'portal/profile', method: 'GET' })
				.then(resp => {
					if (resp.data.success) {
						commit('PORTAL_SET_USER', resp.data.data)
					}
					resolve(resp.data)
				})
				.catch(err => reject(err))
		})
	},

	PORTAL_FETCH_UNREAD: ({ commit }) => {
		return Vue.axios({ url: 'portal/notifications/unread-count', method: 'GET' })
			.then(resp => {
				if (resp.data.success) {
					commit('PORTAL_SET_UNREAD', resp.data.data.count)
				}
			})
			.catch(() => {})
	},

	PORTAL_RESTORE_SESSION: ({ state }) => {
		const token = localStorage.getItem('portal-token')
		if (token) {
			Vue.axios.defaults.headers = {
				'Content-Type': 'application/json',
				'Accept': 'application/json',
				'Authorization': 'Bearer ' + token
			}
		}
		const companyId = localStorage.getItem('portal-company-id')
		if (companyId) {
			Vue.axios.defaults.headers['X-Portal-Company'] = companyId
		}
	}
}

// Azzera la sessione portale senza toccare la navigazione. Usata sia dal logout sia
// dall'accesso da impersonazione, che deve partire da uno stato pulito: gli header di
// axios sono globali e un X-Portal-Company rimasto dall'utente precedente verrebbe
// inviato con il token nuovo.
function clearPortalSession(state) {
	state.token = ''
	state.user = null
	state.companies = []
	state.currentCompanyId = null
	state.forcePasswordChange = false
	state.unreadNotifications = 0
	localStorage.removeItem('portal-token')
	localStorage.removeItem('portal-user')
	localStorage.removeItem('portal-force-pw')
	localStorage.removeItem('portal-companies')
	localStorage.removeItem('portal-company-id')
	delete Vue.axios.defaults.headers['X-Portal-Company']
}

const mutations = {
	PORTAL_REQUEST: (state) => {
		state.status = 'loading'
	},

	PORTAL_CLEAR_SESSION: (state) => {
		clearPortalSession(state)
	},

	PORTAL_SUCCESS: (state, data) => {
		state.status = 'success'
		state.token = data.token
		state.user = data.user
		state.forcePasswordChange = data.force_password_change || false

		// Companies
		state.companies = data.companies || (data.company ? [data.company] : [])
		localStorage.setItem('portal-companies', JSON.stringify(state.companies))

		// Auto-select se 1 sola company
		if (state.companies.length === 1) {
			state.currentCompanyId = state.companies[0].id
			localStorage.setItem('portal-company-id', state.companies[0].id)
		} else {
			state.currentCompanyId = null
			localStorage.removeItem('portal-company-id')
		}

		if (data.force_password_change) {
			localStorage.setItem('portal-force-pw', 'true')
		} else {
			localStorage.removeItem('portal-force-pw')
		}

		localStorage.setItem('portal-token', data.token)
		localStorage.setItem('portal-user', JSON.stringify(data.user))

		const headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/json',
			'Authorization': 'Bearer ' + data.token
		}
		if (state.currentCompanyId) {
			headers['X-Portal-Company'] = state.currentCompanyId
		}
		Vue.axios.defaults.headers = headers
	},

	PORTAL_ERROR: (state) => {
		state.status = 'error'
	},

	PORTAL_LOGOUT: (state) => {
		clearPortalSession(state)

		// Accept resta: una richiesta ancora in volo dopo il logout (es. sessione
		// chiusa dal server mentre la dashboard carica) deve ricevere un 401 JSON,
		// non il redirect a /login che il browser blocca come errore di rete.
		Vue.axios.defaults.headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/json',
			'Authorization': ''
		}

		router.push({ name: 'portal-login' }).catch(err => {
			if (err.name !== 'NavigationDuplicated') {
				throw err
			}
		})
	},

	PORTAL_SET_USER: (state, data) => {
		state.user = data.user
		state.forcePasswordChange = data.portal_profile ? data.portal_profile.force_password_change : false
		if (state.forcePasswordChange) {
			localStorage.setItem('portal-force-pw', 'true')
		} else {
			localStorage.removeItem('portal-force-pw')
		}
		localStorage.setItem('portal-user', JSON.stringify(data.user))

		// Aggiorna companies se presenti nella risposta profile
		if (data.companies && data.companies.length) {
			state.companies = data.companies
			localStorage.setItem('portal-companies', JSON.stringify(state.companies))
		}
	},

	PORTAL_SET_UNREAD: (state, count) => {
		state.unreadNotifications = count
	},

	PORTAL_SET_COMPANY: (state, companyId) => {
		state.currentCompanyId = Number(companyId)
		localStorage.setItem('portal-company-id', companyId)
		Vue.axios.defaults.headers['X-Portal-Company'] = companyId
	},

	PORTAL_UPDATE_COMPANY_ROLE: (state, { companyId, role }) => {
		const company = state.companies.find(c => c.id == companyId)
		if (company) {
			company.role = role
			localStorage.setItem('portal-companies', JSON.stringify(state.companies))
		}
	},

	PORTAL_UPDATE_COMPANY_DATA: (state, { companyId, data }) => {
		const company = state.companies.find(c => c.id == companyId)
		if (company) {
			if (data.role) company.role = data.role
			if (data.can_upload !== undefined) company.can_upload = data.can_upload
			localStorage.setItem('portal-companies', JSON.stringify(state.companies))
		}
	},

	PORTAL_CLEAR_FORCE_PASSWORD: (state) => {
		state.forcePasswordChange = false
		localStorage.removeItem('portal-force-pw')
	},

	PORTAL_SET_FORCE_PASSWORD: (state) => {
		state.forcePasswordChange = true
		localStorage.setItem('portal-force-pw', 'true')
	},

	PORTAL_SET_SESSION_EXPIRED: (state) => {
		state.sessionExpired = true
	},

	PORTAL_CLEAR_SESSION_EXPIRED: (state) => {
		state.sessionExpired = false
	}
}

export default {
	state,
	getters,
	actions,
	mutations
}
